import { copyNode, findNode, getBlankTree, moveNode } from "../tree/tree";
import {
  loadBuilderElementsFromWordPressWithPromise,
  loadDocumentFromWordPress
} from "../wordpress/load";
import { loadSingularitySectionLibrary } from "./page-generation";
import { fold } from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";
import { getSectionToInsert } from "./singularity";
import { saveTreeToWP, updateSingularityMeta } from "./step1/step1";
import { syncRewrite } from "./sections";
import { Tree, TreeNode } from "@/types/tree";
import { getPromptInfoFromNode } from "./util";
import {
  getFieldsToComplete,
  getImagesToComplete,
  handleAiError
} from "../ai/ai";
import { formatFieldsForOpenAI, getPrompt } from "../ai/prompts";
import { globalStore } from "@/store/store";
import lodash from "lodash";
import { callImageService } from "@/futurelayer/api/image-bridge";

export async function behindTheScenesPageGeneration(
  pageId: number,
  abortController: AbortController
) {
  const initialSections = pipe(
    await loadDocumentFromWordPress(pageId),
    fold(
      () => {
        return undefined;
      },
      loadedDoc => {
        if (
          "document" in loadedDoc &&
          loadedDoc.document.documentMeta.singularityMeta
        ) {
          return loadedDoc.document.documentMeta.singularityMeta
            .singularityInitialSections
            ? loadedDoc.document.documentMeta.singularityMeta
                .singularityInitialSections
            : undefined;
        } else {
          return undefined;
        }
      }
    )
  );

  if (!initialSections) {
    console.error("No initial sections found for page ID:", pageId);
    return;
  }

  // need to do this before we can use the tree
  await loadBuilderElementsFromWordPressWithPromise();

  // might be unnecessary as this might already be loaded
  await loadSingularitySectionLibrary();

  // now let's populate the page
  const blankTree = getBlankTree();

  for (let index = 0; index < initialSections.length; index++) {
    const section = initialSections[index];

    const sectionToInsert = getSectionToInsert(section);
    if (!sectionToInsert) continue;

    const copiedNode = copyNode(blankTree, sectionToInsert);
    moveNode(blankTree, copiedNode.id, blankTree.root.id, index);
  }

  console.log("page populated with sections", blankTree);

  for (const child of blankTree.root.children) {
    console.log("rewriteContentInPhantomSection", child.id);
    await rewriteContentInPhantomSection(blankTree, child.id, abortController);

    console.log("populateImagesInPhantomSection", child.id);
    await populateImagesInPhantomSection(blankTree, child.id, abortController);

    // we need to use the AI job controller for this? at the same time we need to be able to await the AI job controller... like, if we say the job
    // runs immediately, we should be able to await that. right now the aiJobController doens't support that.
  }

  console.log("content rewritten with AI");
  console.log("images updated AI");

  await saveTreeToWP(blankTree, pageId, undefined);

  await updateSingularityMeta(pageId, {
    singularityInitialSections: initialSections,
    initialSectionsAlreadyPopulated: true
  });

  console.log("tree saved and singularity meta updated for page", pageId);
}

export async function rewriteContentInPhantomSection(
  containingTree: Tree,
  sectionId: TreeNode["id"],
  abortController: AbortController
) {
  const node = findNode(containingTree, sectionId);
  console.log("found node", node);

  if (!node) return;

  const promptInfo = getPromptInfoFromNode(node);

  const fields = getFieldsToComplete(node);
  const fieldsToFill = formatFieldsForOpenAI(fields);
  const prompt = getPrompt(
    fieldsToFill,
    promptInfo,
    globalStore.aiSettings.prompt
  );

  console.log({ fields, fieldsToFill, prompt });

  if (!fields.length) return;

  try {
    const parsedCompletion = await syncRewrite(prompt, fields, abortController);

    console.log("parsedCompletion", parsedCompletion);

    parsedCompletion.elements.forEach(completedField => {
      const nodeToModify = findNode(containingTree, completedField.nodeId);
      if (nodeToModify) {
        lodash.set(
          nodeToModify.data.properties,
          completedField.controlPath,
          completedField.value
        );
      }
    });

    promptInfo.singularity.shouldGenerateOnInitialLoad = false;
    promptInfo.singularity.unsplashKeywords =
      parsedCompletion.unsplash_keywords;

    lodash.set(node.data.properties, "meta.promptInfo", promptInfo);
  } catch (error) {
    handleAiError(error as string);
  }
}

export async function populateImagesInPhantomSection(
  containingTree: Tree,
  sectionId: TreeNode["id"],
  abortController: AbortController
) {

  alert('dont use this, use replaceImagesInSectionWithAiMutating instead');
  const node = findNode(containingTree, sectionId);

  if (!node) return;

  const promptInfo = getPromptInfoFromNode(node);

  const unsplashKeywords = promptInfo.singularity.unsplashKeywords.length
    ? promptInfo.singularity.unsplashKeywords
    : ["generic"];

  const imageFieldsToComplete = getImagesToComplete(node);

  if (!(imageFieldsToComplete.length > 0)) return;

  const unsplashResults = await callImageService({
    query: unsplashKeywords.join(", "),
    page: 1,
    per_page: imageFieldsToComplete.length
  }, abortController);

  console.log("unsplashResults", unsplashResults);
  console.log("imageFieldsToComplete", imageFieldsToComplete);

  for (let index = 0; index < imageFieldsToComplete.length; index++) {
    const imageFieldToComplete = imageFieldsToComplete[index];
    const img = unsplashResults[index];

    const nodeToUpdate = findNode(containingTree, imageFieldToComplete.nodeId);

    console.log("nodeToUpdate", nodeToUpdate);

    if (!nodeToUpdate) continue;

    lodash.set(nodeToUpdate.data.properties, imageFieldToComplete.controlPath, {
      id: -1,
      type: "external_image",
      url: img.urls.regular,
      alt: img.alt_description || "",
      caption: img.description || ""
    });

    console.log(
      `Updated node ${nodeToUpdate.id} with image from Unsplash: ${img.urls.regular} at control path ${imageFieldToComplete.controlPath}`
    );
  }
}
